import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { z } from 'zod';

import { errorMessage, useCompleteZohoInstall } from '../../../../api/integrations.js';
import { LoadingArea, StartPageLink, TextPage } from '../../../../components/PlaceholderPage.js';

/**
 * `/integrations/zoho/callback`: where Zoho sends the browser back after
 * "Connect Zoho CRM" (`features/integrations`).
 */
const searchSchema = z.object({
  code: z.string().optional(),
  state: z.string().optional(),
  error: z.string().optional(),
});

export const Route = createFileRoute('/_app/integrations/zoho/callback')({
  validateSearch: (search: Record<string, unknown>) => {
    const parsed = searchSchema.safeParse(search);
    return parsed.success ? parsed.data : {};
  },
  component: ZohoCallbackPage,
});

const TITLE = "Zoho CRM wasn't connected";

function ZohoCallbackPage() {
  const { code, state, error } = Route.useSearch();
  const navigate = useNavigate();
  const complete = useCompleteZohoInstall();
  const sent = useRef(false);

  const canComplete = error === undefined && code !== undefined && state !== undefined;

  useEffect(() => {
    if (!canComplete || sent.current) return;
    sent.current = true;
    complete.mutate(
      { code, state },
      {
        onSuccess: ({ websiteId }) =>
          void navigate({
            to: '/websites/$websiteId/integrations',
            params: { websiteId },
            search: { integration: 'zoho' },
            replace: true,
          }),
      },
    );
  }, [canComplete, code, state, complete, navigate]);

  if (error !== undefined) {
    return (
      <TextPage title={TITLE} action={<StartPageLink />}>
        {error === 'access_denied'
          ? 'Groundly wasn’t allowed to access Zoho CRM. Connect Zoho CRM again from the website’s Integrations when you’re ready.'
          : 'Zoho CRM stopped before the connection was made. Connect Zoho CRM again from the website’s Integrations.'}
      </TextPage>
    );
  }
  if (!canComplete) {
    return (
      <TextPage title={TITLE} action={<StartPageLink />}>
        This page is only for coming back from Zoho CRM. Connect Zoho CRM from the website&apos;s
        Integrations.
      </TextPage>
    );
  }
  if (complete.isError) {
    return (
      <TextPage title={TITLE} action={<StartPageLink />}>
        {errorMessage(
          complete.error,
          'Groundly couldn’t reach the server. Connect Zoho CRM again.',
        )}
      </TextPage>
    );
  }
  return <LoadingArea />;
}
